import { Alert, Button, Modal } from "flowbite-react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { ADMIN, TAGS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import Input from "../inputs/Input";
import DrawerLoading from "../DrawerLoading";
import SelectInput from "../inputs/SelectInput";
import CurrencyInput from "../inputs/CurrencyInput";
import { Tooltip } from "react-tooltip";
import { FaRegQuestionCircle } from "react-icons/fa";
import { RiRobot2Line } from "react-icons/ri";
import { formatToReal } from "../../utils/utils";
import MultiSelectPopover from "../inputs/MultiSelectPopover";

const EditQueryCustomForm = ({
  categories,
  company,
  fetchQueries,
  onClose,
  isOpen,
  query, // { id, ... }
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // ---- TAGS state ----
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tagSearch, setTagSearch] = useState("");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);

  // ---- Transfer list state (adicionais) ----
  const [selectedAdditionalIds, setSelectedAdditionalIds] = useState([]);
  const [leftSelected, setLeftSelected] = useState([]);
  const [rightSelected, setRightSelected] = useState([]);
  const [searchLeft, setSearchLeft] = useState("");
  const [searchRight, setSearchRight] = useState("");

  // Providers (para Campos de retorno)
  const { isLoading: isLoadingProviders, data: providers } = useQuery(
    ["providers", company, isOpen],
    async () => {
      const { data: items } = await axios.get(`${ADMIN.GET_PROVIDERS_ALL}`);
      return items.data;
    },
    { enabled: isOpen },
  );

  // Companies
  const { isLoading: isLoadingCompanies, data: companiesPayload } = useQuery(
    ["companiesOptions", isOpen],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`,
      );
      return data?.data?.data ?? data?.data ?? [];
    },
    { enabled: isOpen },
  );

  const companiesOptions = useMemo(() => {
    const seen = new Set();
    const list =
      (companiesPayload || [])
        .filter((c) => {
          const typeId = String(c?.companyTypeId || "").toUpperCase();
          const typeName = String(c?.CompanyType?.name || "").toUpperCase();
          return typeId.includes("REPRESENT") || typeName.includes("REPRESENT");
        })
        .map((c) => {
          const name =
            c?.typeCustomer === "CPF"
              ? c?.name
              : c?.socialReason || c?.name || c?.companyName;
          return { id: c.id, name: name || `#${c.id}` };
        }) ?? [];
    return list
      .filter((x) => x.id != null)
      .filter((x) => {
        if (seen.has(x.id)) return false;
        seen.add(x.id);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [companiesPayload]);

  // Detalhe da query
  const { isLoading: isLoadingQueryCustom, data: queryCustom } = useQuery(
    ["queryCustom", query?.id, isOpen],
    async () => {
      const { data: items } = await axios.get(
        `${ADMIN.GET_QUERY}/${query?.id}`,
      );
      return items.data;
    },
    {
      enabled: isOpen && !!query?.id,
      staleTime: 0,
      refetchOnMount: "always",
    },
  );

  // Lista de tags disponíveis
  const {
    isLoading: isLoadingTags,
    data: tagsPayload,
    error: tagsError,
  } = useQuery(
    ["admin-tags", isOpen],
    async () => {
      const { data } = await axios.get(`${TAGS.GET_ALL_SIMPLE}`);
      return Array.isArray(data?.data) ? data.data : data;
    },
    { enabled: isOpen },
  );

  // Lista de consultas adicionais (catálogo) — todas as queries adicionais não-modulares
  const { isLoading: isLoadingAdditionalCatalog, data: additionalCatalog } =
    useQuery(
      ["additionalQueriesCatalog", isOpen],
      async () => {
        const { data } = await axios.get(
          `${ADMIN.GET_QUERIES}?isModular=0&isAdditional=1&itemsPerPage=-1`,
        );
        return data?.data?.data ?? [];
      },
      { enabled: isOpen },
    );

  const allTags = useMemo(() => tagsPayload || [], [tagsPayload]);

  // Sempre buscar dados frescos ao abrir o modal
  useEffect(() => {
    if (!isOpen || !query?.id) return;
    queryClient.invalidateQueries(["queryCustom", query?.id], {
      exact: true,
      refetchActive: true,
      refetchInactive: true,
    });
    queryClient.invalidateQueries(["admin-tags"], {
      exact: false,
      refetchActive: true,
      refetchInactive: true,
    });
    queryClient.invalidateQueries(["additionalQueriesCatalog"], {
      exact: false,
      refetchActive: true,
      refetchInactive: true,
    });
    queryClient.invalidateQueries(["providers"], {
      exact: false,
      refetchActive: true,
      refetchInactive: true,
    });
    queryClient.invalidateQueries(["companiesOptions"], {
      exact: false,
      refetchActive: true,
      refetchInactive: true,
    });
  }, [isOpen, query?.id, queryClient]);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    control,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      query: {
        name: "",
        price: 0,
        url: "",
        categoryId: "",
        description: "",
        status: 1,
        isAdditional: false,
      },
      queryCustomized: {
        parameterType: "",
        showAlertsBoard: false,
        hasIA: false,
        hasTabs: false,
      },
      fields: [],
    },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: "fields",
  });

  // Helper: obter tags existentes de vários formatos
  const getExistingTagIds = (qc) => {
    if (!qc) return [];
    if (Array.isArray(qc.Tags)) return qc.Tags.map((t) => t.id);
    if (Array.isArray(qc.tags)) return qc.tags.map((t) => t.id);
    if (qc.Query && Array.isArray(qc.Query.Tags))
      return qc.Query.Tags.map((t) => t.id);
    return [];
  };

  const getExistingCompanyIds = (qc) => {
    if (!qc) return [];
    if (Array.isArray(qc.Companies)) return qc.Companies.map((c) => c.id);
    if (Array.isArray(qc.companies))
      return qc.companies.map((c) => c.id || c.companyId);
    if (Array.isArray(qc.companyIds)) return qc.companyIds.map(Number);
    if (Array.isArray(qc.CompanyQueryPermissions))
      return qc.CompanyQueryPermissions.map((c) => c.CompanyId || c.companyId);
    return [];
  };

  // Carrega e reseta valores ao abrir com dados prontos
  useEffect(() => {
    if (!isOpen || !query?.id || !queryCustom) return;

    const customFields =
      queryCustom?.QueryCustomized?.QueryCustomizedFields || [];
    const existingAdditionalIds = (queryCustom?.additionalQueries || []).map(
      (q) => q.id,
    );

    const defaults = {
      query: {
        name: queryCustom.name || "",
        price: queryCustom.price || 0,
        url: queryCustom.url || "",
        categoryId: String(queryCustom.categoryId ?? ""),
        queryCategory: queryCustom.queryCategory || "",
        description: queryCustom.description || "",
        status: queryCustom.status ?? 1,
        isAdditional: !!queryCustom.isAdditional,
      },
      queryCustomized: {
        parameterType: queryCustom?.QueryCustomized?.parameterType || "",
        showAlertsBoard: !!queryCustom?.QueryCustomized?.showAlertsBoard,
        hasIA: !!queryCustom?.QueryCustomized?.hasIA,
        hasTabs: !!queryCustom?.QueryCustomized?.hasTabs,
      },
      fields: customFields.map((f, idx) => ({
        priority: idx + 1,
        providerId: f.providerId ?? f.ProviderId,
        queryId: f.queryId ?? f.QueryId,
        ignoreErrors: f.ignoreErrors ?? true,
        isUniqueProvider: f.isUniqueProvider ?? false,
      })),
    };

    reset(defaults, { keepDirtyValues: false, keepErrors: false });

    // sincroniza estados auxiliares
    setSelectedTagIds(getExistingTagIds(queryCustom));
    setSelectedCompanyIds(getExistingCompanyIds(queryCustom));
    setSelectedAdditionalIds(existingAdditionalIds);
    setLeftSelected([]);
    setRightSelected([]);
    setSearchLeft("");
    setSearchRight("");
  }, [isOpen, query?.id, queryCustom, reset]);

  // Atualiza prioridade visual dos fields após qualquer alteração do array
  useEffect(() => {
    fields.forEach((_, index) => {
      setValue(`fields[${index}].priority`, index + 1);
    });
  }, [fields, setValue]);

  // Filtro local por nome de tag
  const filteredTags = useMemo(() => {
    const s = tagSearch.trim().toLowerCase();
    if (!s) return allTags;
    return allTags.filter((t) => t.name.toLowerCase().includes(s));
  }, [allTags, tagSearch]);

  // Helpers de tag
  const toggleTag = (id) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };
  const selectAllFiltered = () => {
    const onlyIds = (filteredTags || []).map((t) => t.id);
    setSelectedTagIds((prev) => Array.from(new Set([...prev, ...onlyIds])));
  };
  const clearAllTags = () => setSelectedTagIds([]);

  // ---- Transfer list (adicionais) ----
  const isAdditional = watch("query.isAdditional");
  const categoryId = watch("query.categoryId");

  // Candidatas: catálogo filtrado pela mesma categoria da consulta e isAdditional=true
  const candidateAdditionals = useMemo(() => {
    const list = Array.isArray(additionalCatalog) ? additionalCatalog : [];
    return list
      .filter(
        (q) => q?.isAdditional && String(q?.categoryId) === String(categoryId),
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [additionalCatalog, categoryId]);

  const leftList = useMemo(() => {
    const term = searchLeft.trim().toLowerCase();
    return candidateAdditionals
      .filter((q) => !selectedAdditionalIds.includes(q.id))
      .filter((q) => (term ? q.name.toLowerCase().includes(term) : true));
  }, [candidateAdditionals, selectedAdditionalIds, searchLeft]);

  const rightList = useMemo(() => {
    const term = searchRight.trim().toLowerCase();
    return selectedAdditionalIds
      .map((id) => candidateAdditionals.find((q) => q.id === id))
      .filter(Boolean)
      .filter((q) => (term ? q.name.toLowerCase().includes(term) : true));
  }, [candidateAdditionals, selectedAdditionalIds, searchRight]);

  const moveRight = () => {
    setSelectedAdditionalIds((prev) =>
      Array.from(new Set([...prev, ...leftSelected])),
    );
    setLeftSelected([]);
  };
  const moveLeft = () => {
    setSelectedAdditionalIds((prev) =>
      prev.filter((id) => !rightSelected.includes(id)),
    );
    setRightSelected([]);
  };
  const moveAllRight = () => {
    setSelectedAdditionalIds((prev) =>
      Array.from(new Set([...prev, ...leftList.map((q) => q.id)])),
    );
    setLeftSelected([]);
  };
  const moveAllLeft = () => {
    setSelectedAdditionalIds([]);
    setRightSelected([]);
  };

  // Se virar "adicional", limpa seleção (pois adicionais só valem para consulta principal)
  useEffect(() => {
    if (isAdditional) {
      setSelectedAdditionalIds([]);
      setLeftSelected([]);
      setRightSelected([]);
      setSearchLeft("");
      setSearchRight("");
    }
  }, [isAdditional]);

  const validateDuplicateFields = (fields) => {
    const seen = new Set();
    const duplicates = [];
    (fields || []).forEach((field, index) => {
      const id = String(field?.queryId || "");
      if (id && seen.has(id)) duplicates.push(index);
      seen.add(id);
    });
    if (duplicates.length > 0) {
      duplicates.forEach((index) => {
        setError(`fields.${index}.queryId`, {
          type: "manual",
          message: "Conteúdo duplicado",
        });
      });
      return true;
    }
    return false;
  };

  // Submit
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    setSubmitError(null);
    clearErrors("fields");

    // valida duplicados em fields[].queryId
    if (validateDuplicateFields(data.fields)) {
      setIsSubmitting(false);
      return;
    }

    // monta payload
    const cleanedAdditionalIds = isAdditional
      ? []
      : Array.from(new Set(selectedAdditionalIds))
          .map(Number)
          .filter((rid) => rid && rid !== Number(query?.id)); // evita self-link

    const payload = {
      ...data,
      tagIds: selectedTagIds,
      additionalQueryIds: cleanedAdditionalIds,
      companyIds: selectedCompanyIds,
    };

    try {
      await axios.put(`${ADMIN.GET_CUSTOMIZED_QUERY}/${query?.id}`, payload);
      queryClient.invalidateQueries(["queryCustom", query?.id], {
        exact: false,
      });
      onClose(false);
      fetchQueries?.();
      reset(); // limpa form
    } catch (err) {
      setSubmitError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Substitua seu toCents por este:
  const toCents = (v) => {
    if (v == null) return 0;
    const s = String(v).trim();

    // Se tiver vírgula, tratamos formato PT-BR: 1.234,56 -> 1234.56
    const normalized = s.includes(",")
      ? s.replace(/\./g, "").replace(",", ".")
      : s; // "1.00" permanece "1.00"

    const n = parseFloat(normalized);
    if (!Number.isFinite(n)) return 0;
    return Math.round(n * 100);
  };

  const watchedFields = useWatch({ control, name: "fields" }) || [];

  // Índice: "providerId:queryId" -> price (em centavos)
  const priceIndexCents = useMemo(() => {
    const map = new Map();
    (providers || []).forEach((p) => {
      (p?.associatedQueries || []).forEach((q) => {
        const key = `${Number(p.id)}:${Number(q.id)}`;
        const raw = q?.QueryProvider?.price; // ex.: "1.00"
        map.set(key, toCents(raw));
      });
    });
    return map;
  }, [providers]);

  // Soma dos preços selecionados (em centavos) -> converte para reais no final
  const costPrice = useMemo(() => {
    if (!Array.isArray(watchedFields) || priceIndexCents.size === 0) return 0;

    const sumCents = watchedFields.reduce((sum, f) => {
      const key = `${Number(f?.providerId)}:${Number(f?.queryId)}`;
      return sum + (priceIndexCents.get(key) || 0);
    }, 0);

    return sumCents / 100; // volta para reais
  }, [watchedFields, priceIndexCents]);

  const isFetchingFormData =
    isLoadingQueryCustom ||
    !queryCustom ||
    isLoadingProviders ||
    isLoadingTags ||
    isLoadingAdditionalCatalog ||
    isLoadingCompanies;

  return (
    <Modal
      show={isOpen}
      onClose={() => onClose(false)}
      size="5xl"
      // Força remontagem ao abrir/fechar por id (fallback; geralmente nem precisa com o reset)
      key={`${query?.id}-${isOpen ? "open" : "closed"}`}
    >
      <Modal.Header className="py-2 flex items-center bg-gray-50">
        <span className="text-base">Editar consulta personalizada</span>
      </Modal.Header>
      <Modal.Body>
        {isFetchingFormData ? (
          <DrawerLoading />
        ) : (
          <form
            className="flex flex-col gap-4"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Input
              register={register}
              label="Nome da consulta *"
              name="query.name"
              required={true}
            />
            <Input
              register={register}
              label="Descrição da consulta"
              name="query.description"
            />

            <div className="flex flex-col md:flex-row gap-4 md:gap-2">
              <div className="flex-1">
                <Input
                  register={register}
                  label="URL amigável *"
                  name="query.url"
                  required={true}
                  disabled={true}
                />
                <small style={{ fontSize: "10px" }}>
                  Obs: Não é possível alterar a URL amigável
                </small>
              </div>
              <div className="flex-1">
                <CurrencyInput
                  register={register}
                  label="Preço da consulta *"
                  name="query.price"
                  required={true}
                  control={control}
                />

                {/* Preço de custo (soma dos conteúdos selecionados) */}
                <div className="mt-1 text-xs text-gray-600">
                  <span className="font-medium">Preço de custo:</span>{" "}
                  {formatToReal(costPrice)}
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 md:gap-2">
              <SelectInput
                register={register}
                name="query.categoryId"
                label="Categoria da consulta *"
                required={true}
              >
                {categories?.map((category, index) => (
                  <option key={index} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </SelectInput>

              <SelectInput
                register={register}
                name="queryCustomized.parameterType"
                label="Parâmetro da consulta *"
                required={true}
              >
                {watch("query.categoryId") === "1" && (
                  <>
                    <option value="CPF">CPF</option>
                    <option value="CNPJ">CNPJ</option>
                    <option value="CPF/CNPJ">CPF/CNPJ</option>
                  </>
                )}
                {watch("query.categoryId") === "2" && (
                  <>
                    <option value="CPF">CPF</option>
                    <option value="CNPJ">CNPJ</option>
                    <option value="CPF/CNPJ">CPF/CNPJ</option>
                  </>
                )}
                {watch("query.categoryId") === "3" && (
                  <>
                    <option value="PLACA">Placa</option>
                    <option value="PLACA/CHASSI">Placa/Chassi</option>
                    <option value="PLACA/ESTADO">Placa/Estado</option>
                    <option value="PLACA/CHASSI/ESTADO">
                      Placa/Chassi/Estado
                    </option>
                    <option value="CPF/CNPJ">CPF/CNPJ</option>
                  </>
                )}
              </SelectInput>

              <SelectInput
                register={register}
                name="query.status"
                label="Status da consulta *"
                required={true}
              >
                <option value={1}>Ativo</option>
                <option value={0}>Em manutenção</option>
                <option value={-1}>Inativo</option>
              </SelectInput>
              <Input
                register={register}
                label="Categoria de listagem"
                name="query.queryCategory"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {/* Toggle: É consulta adicional? */}
              <div className="flex items-center mb-1">
                <input
                  id="query.isAdditional"
                  type="checkbox"
                  {...register("query.isAdditional")}
                  className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                />
                <label
                  htmlFor="query.isAdditional"
                  className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                >
                  É consulta adicional?
                </label>
              </div>

              {/* Mostrar quadro/IA apenas quando NÃO for adicional */}
              {!isAdditional && (
                <>
                  <div className="flex items-center mb-1">
                    <input
                      id="queryCustomized.showAlertsBoard"
                      type="checkbox"
                      {...register("queryCustomized.showAlertsBoard")}
                      className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <label
                      htmlFor="queryCustomized.showAlertsBoard"
                      className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Mostrar quadro de avisos na consulta?
                    </label>
                  </div>

                  <div className="flex items-center mb-1">
                    <input
                      id="queryCustomized.hasIA"
                      type="checkbox"
                      {...register("queryCustomized.hasIA")}
                      className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <label
                      htmlFor="queryCustomized.hasIA"
                      className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Mostrar sugestão de IA na consulta?{" "}
                      <RiRobot2Line className="inline-block ml-1 text-blue-700 text-base" />
                    </label>
                  </div>
                  <div className="flex items-center mb-1">
                    <input
                      id="queryCustomized.hasTabs"
                      type="checkbox"
                      {...register("queryCustomized.hasTabs")}
                      className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <label
                      htmlFor="queryCustomized.hasTabs"
                      className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >
                      Exibir resultados em abas
                    </label>
                  </div>
                </>
              )}
            </div>

            {/* Campos de retorno */}
            <div className="border border-gray-300 rounded-md p-2">
              <div className="flex gap-2 justify-between items-center mb-4">
                <div className="font-semibold text-sm">Campos de retorno</div>
                <div className="flex gap-2">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      append({
                        priority: fields.length + 1,
                        queryId: "",
                        providerId: "",
                        ignoreErrors: true,
                        isUniqueProvider: false,
                      });
                    }}
                    className="rounded-full w-6 h-6 bg-primary text-white flex items-center justify-center shadow-md"
                  >
                    +
                  </button>
                </div>
              </div>

              {fields.map((field, index) => {
                const selectedProvider = providers?.find(
                  (p) => p.id === Number(watch(`fields[${index}].providerId`)),
                );

                return (
                  <div
                    key={field.id || index}
                    className={`mb-5 ${
                      index !== fields.length - 1
                        ? "border-b border-gray-200 pb-3"
                        : ""
                    }`}
                  >
                    <div className="flex gap-2 mb-2 items-start">
                      <div className="w-52">
                        <SelectInput
                          register={register}
                          name={`fields[${index}].priority`}
                          label="Ordem"
                          required={true}
                        >
                          {Array.from({ length: 20 }).map((_, i) => (
                            <option key={i} value={i + 1}>
                              {i + 1}
                            </option>
                          ))}
                        </SelectInput>
                      </div>

                      <SelectInput
                        register={register}
                        name={`fields[${index}].providerId`}
                        label="Provedor *"
                        required={true}
                      >
                        <option value="">Selecione um Provedor</option>
                        {providers?.map((provider) => (
                          <option key={provider.id} value={provider.id}>
                            {provider.id} - {provider.name}
                          </option>
                        ))}
                      </SelectInput>

                      <SelectInput
                        register={register}
                        name={`fields[${index}].queryId`}
                        label="Conteúdo *"
                        required={true}
                        error={errors?.fields?.[index]?.queryId?.message}
                      >
                        <option value="">Selecione uma consulta</option>
                        {selectedProvider?.associatedQueries
                          ?.filter(
                            (item) =>
                              watch("query.categoryId") == item.categoryId,
                          )
                          ?.map((qopt) => (
                            <option key={qopt.id} value={qopt.id}>
                              {qopt.name} -{" "}
                              {formatToReal(qopt?.QueryProvider?.price || 0)}
                            </option>
                          ))}
                      </SelectInput>

                      {fields.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            remove(index);
                          }}
                          className="w-6 min-w-6 h-6 mt-3 rounded-full bg-red-600 text-white flex items-center justify-center"
                          title="Remover campo"
                        >
                          –
                        </button>
                      )}
                    </div>

                    <div className="flex flex-col md:flex-row gap-5">
                      <div className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          id={`fields[${index}].ignoreErrors`}
                          {...register(`fields[${index}].ignoreErrors`)}
                          className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                        />
                        <label
                          htmlFor={`fields[${index}].ignoreErrors`}
                          className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                        >
                          Ignorar erro de retorno
                        </label>
                      </div>
                      <div className="flex items-center mb-1">
                        <input
                          type="checkbox"
                          id={`fields[${index}].isUniqueProvider`}
                          {...register(`fields[${index}].isUniqueProvider`)}
                          className="w-4 h-4 text-cyan-600 bg-gray-100 border-gray-300 rounded focus:ring-cyan-500"
                        />
                        <label
                          htmlFor={`fields[${index}].isUniqueProvider`}
                          className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                        >
                          É provedor único?
                        </label>
                        <Tooltip
                          id="tooltip-unique-provider"
                          content={
                            <span>
                              Ao assinalar, indica que a consulta será feita{" "}
                              <br />
                              preferencialmente por um provedor específico.{" "}
                              <br />
                              Se houver falha, o sistema não buscará <br />
                              automaticamente outro provedor disponível.
                            </span>
                          }
                        />
                        <FaRegQuestionCircle
                          className="text-gray-400 ml-1"
                          data-tooltip-id="tooltip-unique-provider"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ================== Transfer List: Consultas Adicionais ================== */}
            {!isAdditional && (
              <div className="border border-gray-300 rounded-md p-3">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-semibold text-sm">
                    Consultas adicionais
                  </div>
                  <small className="text-gray-500">
                    Selecione as consultas adicionais que compõem esta consulta
                    principal
                  </small>
                </div>

                {!categoryId && (
                  <Alert color="warning" className="mb-3">
                    Selecione a <b>categoria</b> para listar as consultas
                    adicionais disponíveis.
                  </Alert>
                )}

                <div className="flex gap-3">
                  {/* Esquerda: candidatas */}
                  <div className="border rounded-md p-2 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600">
                        Disponíveis ({leftList.length})
                      </span>
                      <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchLeft}
                        onChange={(e) => setSearchLeft(e.target.value)}
                        className="rounded-md border border-gray-300 p-1 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div className="h-52 overflow-auto border rounded">
                      <ul className="text-sm">
                        {leftList.map((q) => (
                          <li
                            key={q.id}
                            className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4"
                              checked={leftSelected.includes(q.id)}
                              onChange={(e) => {
                                setLeftSelected((prev) =>
                                  e.target.checked
                                    ? [...prev, q.id]
                                    : prev.filter((id) => id !== q.id),
                                );
                              }}
                              id={`left-${q.id}`}
                            />
                            <label
                              htmlFor={`left-${q.id}`}
                              className="cursor-pointer flex-1"
                            >
                              {q.name}
                            </label>
                          </li>
                        ))}
                        {leftList.length === 0 && (
                          <li className="text-xs text-gray-500 px-2 py-2">
                            Nenhum item
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>

                  {/* Centro: botões */}
                  <div className="flex flex-col items-center justify-center gap-2">
                    <Button
                      size="xs"
                      color="light"
                      onClick={moveRight}
                      disabled={leftSelected.length === 0}
                    >
                      &gt;
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={moveAllRight}
                      disabled={leftList.length === 0}
                    >
                      &gt;&gt;
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={moveLeft}
                      disabled={rightSelected.length === 0}
                    >
                      &lt;
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={moveAllLeft}
                      disabled={selectedAdditionalIds.length === 0}
                    >
                      &lt;&lt;
                    </Button>
                  </div>

                  {/* Direita: selecionadas */}
                  <div className="border rounded-md p-2 flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600">
                        Selecionadas ({rightList.length})
                      </span>
                      <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchRight}
                        onChange={(e) => setSearchRight(e.target.value)}
                        className="rounded-md border border-gray-300 p-1 text-xs focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                    <div className="h-52 overflow-auto border rounded">
                      <ul className="text-sm">
                        {rightList.map((q) => (
                          <li
                            key={q.id}
                            className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50"
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4"
                              checked={rightSelected.includes(q.id)}
                              onChange={(e) => {
                                setRightSelected((prev) =>
                                  e.target.checked
                                    ? [...prev, q.id]
                                    : prev.filter((id) => id !== q.id),
                                );
                              }}
                              id={`right-${q.id}`}
                            />
                            <label
                              htmlFor={`right-${q.id}`}
                              className="cursor-pointer flex-1"
                            >
                              {q.name}
                            </label>
                          </li>
                        ))}
                        {rightList.length === 0 && (
                          <li className="text-xs text-gray-500 px-2 py-2">
                            Nenhum item
                          </li>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                {rightList.length > 1 && (
                  <small className="text-gray-500 block mt-2">
                    A ordem pode ser definida depois (ex.: via prioridade por
                    vínculo), se necessário.
                  </small>
                )}
              </div>
            )}
            {/* ================== /Transfer List ================== */}

            {/* Tags */}
            <div className="border border-gray-300 rounded-md p-3">
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-semibold text-sm">
                  Tags da consulta{" "}
                  <span className="text-xs text-gray-500">
                    ({selectedTagIds.length} selecionada
                    {selectedTagIds.length === 1 ? "" : "s"})
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Buscar tag..."
                    value={tagSearch}
                    onChange={(e) => setTagSearch(e.target.value)}
                    className="rounded-md border border-gray-300 p-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                  />
                  <Button
                    size="xs"
                    color="light"
                    onClick={selectAllFiltered}
                    disabled={
                      isLoadingTags || (filteredTags || []).length === 0
                    }
                  >
                    Selecionar filtradas
                  </Button>
                  <Button
                    size="xs"
                    color="light"
                    onClick={clearAllTags}
                    disabled={selectedTagIds.length === 0}
                  >
                    Limpar
                  </Button>
                </div>
              </div>

              {isLoadingTags ? (
                <div className="text-sm text-gray-500">Carregando tags...</div>
              ) : tagsError ? (
                <Alert color="failure">Erro ao carregar tags.</Alert>
              ) : (filteredTags || []).length === 0 ? (
                <div className="text-sm text-gray-500">
                  Nenhuma tag encontrada.
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {filteredTags.map((tag) => {
                    const active = selectedTagIds.includes(tag.id);
                    return (
                      <button
                        key={tag.id}
                        type="button"
                        onClick={() => toggleTag(tag.id)}
                        className={`px-3 py-1 rounded-full border text-sm transition-all
                          ${
                            active
                              ? "bg-cyan-600 text-white border-cyan-600"
                              : "bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200"
                          }`}
                        title={tag.slug || tag.name}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {submitError && (
              <Alert withBorderAccent color="failure">
                Ops! Ocorreu um erro ao tentar atualizar esta consulta. Tente
                novamente mais tarde.
              </Alert>
            )}

            {/* Footer buttons dentro do form para submeter por Enter também */}
            <div className="flex gap-2 justify-end">
              <Button
                color="gray"
                type="button"
                onClick={() => {
                  onClose(false);
                  setSelectedTagIds([]);
                  setTagSearch("");
                  setSelectedAdditionalIds([]);
                  setLeftSelected([]);
                  setRightSelected([]);
                  setSelectedCompanyIds([]);
                }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                isProcessing={isSubmitting}
                disabled={isSubmitting}
              >
                Atualizar consulta
              </Button>
            </div>
          </form>
        )}
      </Modal.Body>
      {/* Mantive o Footer do Modal opcionalmente vazio, pois os botões estão no form */}
    </Modal>
  );
};

export default EditQueryCustomForm;
